import { Observable, OperatorFunction, concat, of } from 'rxjs';
import { map, pairwise, shareReplay, takeLast, tap } from 'rxjs/operators';

export function log<T>(text?: string) {
  return tap<T>(x => console.log(text, x));
}

const pending$ = of({ state: 'pending' as const, value: null });

/**
 * Adds running state to an observable stream.
 * Emits an object with state 'pending' and value null at the start if includeFirst is true.
 * Emits an object with state 'running' and the previous value on each emission.
 * Emits an object with state 'finished' and the last value at the end.
 */
export function withRunningState<T>(includeFirst: boolean): OperatorFunction<T, { state: 'pending' | 'running' | 'finished', value: T | null }> {
  return (source: Observable<T>) => {
    const shared$ = source.pipe(shareReplay(1));

    const running$ = shared$.pipe(
      pairwise(),
      map(([value]) => ({
        state: 'running' as const,
        value,
      })),
    );

    const finished$ = shared$.pipe(
      takeLast(1),
      map(value => ({
        state: 'finished' as const,
        value,
      })),
    );

    if (includeFirst) return concat(pending$, running$, finished$);
    return concat(running$, finished$);
  };
}
